import React from 'react';
import { Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  Clock, Tag, Users, ChevronRight,
  AlertCircle, CheckCircle, XCircle,
} from 'lucide-react';
import UserAvatar from '../ui/UserAvatar';
import { getTicketCreatorName } from './supportIdentity';
import './Support.scss';

const timeAgo = (d) => {
  if (!d) return '';
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusLabel = (s) => {
  const map = { open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed' };
  return map[s] || s;
};

const categoryLabel = (c) => {
  const map = { platform_bug: 'Platform Bug', institute_issue: 'Institute Issue', feature_request: 'Feature Request', other: 'Other' };
  return map[c] || c;
};

const statusDotClass = (s) => {
  const map = { open: 'amber', in_progress: 'blue', resolved: 'emerald', closed: 'muted' };
  return map[s] || 'muted';
};

const StatusIcon = ({ status, size = 12 }) => {
  const map = { open: AlertCircle, in_progress: Clock, resolved: CheckCircle, closed: XCircle };
  const Icon = map[status] || AlertCircle;
  return <Icon size={size} />;
};

const TicketTable = ({ tickets = [], loading, basePath = '/support/tickets', showTenant = false, myTicketsOnly = false }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="sp-loading">
        <Spinner animation="border" size="sm" />
        <span>Loading tickets...</span>
      </div>
    );
  }

  if (!tickets.length) return null;

  return (
    <div className="sp-ticket-list">
      {tickets.map((t) => {
        const creatorName = getTicketCreatorName(t);
        const replyCount = t.comments ? t.comments.filter((c) => !c.isInternal).length : 0;

        return (
          <div
            key={t._id}
            className="sp-ticket-card"
            onClick={() => navigate(`${basePath}/${t._id}`)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate(`${basePath}/${t._id}`)}
          >
            <div className={`sp-ticket-card__dot sp-ticket-card__dot--${statusDotClass(t.status)}`} />
            <div className="sp-ticket-card__body">
              <div className="sp-ticket-card__header">
                <div className="sp-ticket-card__header-left">
                  <h4 className="sp-ticket-card__title">{t.title}</h4>
                  {t.description && (
                    <p className="sp-ticket-card__desc">{t.description}</p>
                  )}
                </div>
                <div className="sp-ticket-card__header-right">
                  <div className="sp-ticket-card__badges">
                    <span className="sp-ticket-card__id">{t.ticketId}</span>
                    <span className={`status-badge status-badge--${t.status}`}>
                      <StatusIcon status={t.status} />
                      {statusLabel(t.status)}
                    </span>
                    <span className={`priority-badge priority-badge--${t.priority}`}>{t.priority}</span>
                  </div>
                  <ChevronRight size={16} className="sp-ticket-card__chevron" />
                </div>
              </div>

              <div className="sp-ticket-card__meta">
                {!myTicketsOnly && (
                  <div className="sp-ticket-card__meta-avatar">
                    <UserAvatar name={creatorName} size="sm" />
                    <span>{creatorName}</span>
                  </div>
                )}
                <span className="sp-ticket-card__meta-item">
                  <Tag size={12} /> {categoryLabel(t.category)}
                </span>
                {t.batchName && (
                  <span className="sp-ticket-card__meta-item">
                    <Users size={12} /> {t.batchName}
                  </span>
                )}
                {showTenant && t.tenantName && (
                  <span className="sp-ticket-card__meta-item">{t.tenantName}</span>
                )}
                <span className="sp-ticket-card__meta-item">
                  <Clock size={12} /> {timeAgo(t.updatedAt || t.createdAt)}
                </span>
                {replyCount > 0 && (
                  <span className="sp-ticket-card__meta-item">{replyCount} {replyCount === 1 ? 'reply' : 'replies'}</span>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TicketTable;
